import React, { useEffect, useState, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useParams,
  useNavigate,
  useRouter,
  Navigate,
  useCanGoBack,
} from '@tanstack/react-router';

import {
  ArrowLeft,
  Edit,
  X,
  MapPin,
  Calendar,
  Gauge,
  Copy,
  Wifi,
  Gift,
  Zap,
} from 'lucide-react';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Skeleton,
} from '@/lib/shadcn/components/ui';
import { Button } from '@/lib/shadcn/components/ui/button';

import {
  SharedAlertDialog,
  useAlertDialog,
  PageSkeleton,
  TotalUsageDisplay,
} from '@/shared/components';
import { useToast } from '@/shared/hooks/useToast';
import { useUsageControls } from '@/shared/hooks/useUsageControls';
import { useIsSuperAdmin } from '@/shared/hooks/useIsSuperAdmin';
import { useDashboardTenantFilter } from '@/shared/hooks/useTenantFilter';
import {
  dateComparisons,
  dateValidators,
  dateFormatters,
} from '@/shared/utils/date.utils';
import { getUsageQueryAndResponse } from '@/shared/utils';

import { useCustomerManager } from '../managers';
import { useCustomerMutations } from '../queries/customers.query';
import type { Customer, CustomerDetail } from '../models/customer.model';
import { CustomerNotification } from '../components/CustomerNotification/CustomerNotification';
import { CustomerStripeProductsSection } from '../components';

// Lazy-loaded components
const CustomerUsageChart = React.lazy(
  () => import('../components/CustomerUsageChart/CustomerUsageChart')
);
const CustomerBillingTable = React.lazy(
  () => import('../components/CustomerBillingTable/CustomerBillingTable')
);
const CustomerTelemetryTable = React.lazy(
  () => import('../components/CustomerTelemetryTable/CustomerTelemetryTable')
);
const PowerOfAttorneyPopup = React.lazy(
  () => import('../components/PowerOfAttorneyPopup/PowerOfAttorneyPopup')
);

const statusColors = {
  active: 'bg-green-100 text-green-800 border-green-200',
  inactive: 'bg-gray-100 text-gray-800 border-gray-200',
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
};

export const CustomerDetailPage: React.FC = () => {
  const params = useParams({ strict: false });
  const id = params.id as string;
  // View more toggle: hide heavy sections until requested
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const moreRef = React.useRef<HTMLDivElement | null>(null);

  const { cancelSubscription, giftEnergy } = useCustomerMutations();
  // usage controls state (lifted to page level)
  const {
    usageMode,
    usageYear,
    usageMonth,
    usageDate,
    setUsageMode,
    setUsageYear,
    setUsageMonth,
    setUsageDate,
  } = useUsageControls();

  const [open, setOpen] = useState(false);
  const [popupOpen, setPopupOpen] = React.useState(false);

  const customerManager = useCustomerManager(id);
  const { customer, loading: isLoading } = customerManager;

  const isSuperAdmin = useIsSuperAdmin();
  const { globalTenantId } = useDashboardTenantFilter();
  const usageTenantId = isSuperAdmin ? globalTenantId : null;

  // const { fetching } = customerManager;
  // Call all hooks in stable order; each hook uses `enabled` so only the active one will fetch.
  const dailyUsageQuery = customerManager.useGetCustomerDailyUsageQuery(
    usageMode === 'day' ? usageDate : undefined,
    customer?.id,
    usageTenantId
  );

  const monthlyUsageQuery = customerManager.useGetCustomerMonthlyUsageQuery(
    usageMode === 'month' ? usageYear : undefined,
    usageMode === 'month' ? usageMonth + 1 : undefined,
    customer?.id,
    usageTenantId
  );
  const yearlyUsageQuery = customerManager.useGetCustomerYearlyUsageQuery(
    usageMode === 'year' ? usageYear : undefined,
    customer?.id,
    usageTenantId
  );

  const { usageQuery, usageResp } = getUsageQueryAndResponse(
    usageMode,
    dailyUsageQuery,
    monthlyUsageQuery,
    yearlyUsageQuery
  );

  const cancelSubscriptionDialog = useAlertDialog();
  const navigate = useNavigate();
  const router = useRouter();
  const [isSubscribed, setIsSubscribed] = useState(false);

  // View more toggle: hide heavy sections until requested
  const [inputValue, setInputValue] = useState('');

  const { t } = useTranslation('customers');
  const { notify } = useToast();
  const giftEnergyDialog = useAlertDialog();
  const giftEnergyConfirmDialog = useAlertDialog();
  const canGoBack = useCanGoBack();

  useEffect(() => {
    if (customer) {
      // Determine if customer has an active subscription
      let hasActiveSubscription = false;

      if (customer.subscriptionStatus === 'active') {
        hasActiveSubscription = true;
      } else if (customer.subscriptionEndDate) {
        // Validate the date before using it
        const isValidDate = dateValidators.isValid(
          customer.subscriptionEndDate
        );

        if (isValidDate) {
          hasActiveSubscription =
            !dateComparisons.isPast(customer.subscriptionEndDate) &&
            customer.subscriptionStatus !== 'cancelled';
        } else {
          // If date is invalid, consider subscription inactive
          console.warn(
            'Invalid subscription end date:',
            customer.subscriptionEndDate
          );
          // hasActiveSubscription remains false
        }
      }

      setIsSubscribed(hasActiveSubscription);
    }
  }, [customer]);

  // When the more/details panel is shown, scroll it into view for a smoother, more immersive reveal
  useEffect(() => {
    if (showMoreDetails) {
      const id = window.setTimeout(
        () =>
          moreRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          }),
        80
      );
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [showMoreDetails]);

  if (isLoading)
    return (
      <PageSkeleton
        headerLines={1}
        subtitleLines={1}
        actions={2}
        rows={4}
        cols={2}
      />
    );
  if (!customer) {
    return <Navigate to="/customers" replace />;
  }
  // typed alias to avoid `any` usage and support both legacy `Customer` and flat `CustomerDetail`
  const c = customer as Partial<Customer & CustomerDetail>;

  const getStatusBadgeClass = (status?: string | null) => {
    if (!status) return 'bg-gray-100 text-gray-800 border-gray-200';
    const key = status.toString().toLowerCase() as keyof typeof statusColors;
    return statusColors[key] ?? 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const handleGoBack = () => {
    if (canGoBack) router.history.back();
    router.navigate({ to: '/customers' });
  };

  const handleCancelSubscriptionConfirm = async () => {
    try {
      await cancelSubscription.mutateAsync(customer.id);
    } catch {
      // cancelSubscription onError will show notification; keep dialog open handling by SharedAlertDialog
      console.error('Failed to cancel subscription');
    }
  };

  const handleGiftEnergyConfirm = async () => {
    // Close the input dialog and show confirmation dialog
    giftEnergyDialog.setIsOpen(false);
    giftEnergyConfirmDialog.showAlert();
  };

  const handleGiftEnergyFinalConfirm = async () => {
    try {
      await giftEnergy.mutateAsync({
        id: customer.id,
        payload: { kWh: Number(inputValue) },
      });
      setInputValue('');
      giftEnergyConfirmDialog.setIsOpen(false);
    } catch (error) {
      console.error(error);
    }
  };

  // Device helpers
  const rawDeviceId = c.deviceId ?? '';
  const formatMac = (id: string) => {
    if (!id) return '';
    const hex = id.replace(/[^a-fA-F0-9]/g, '');
    if (hex.length === 12) {
      return (
        hex
          .match(/.{1,2}/g)
          ?.join(':')
          .toUpperCase() ?? id.toUpperCase()
      );
    }
    if (/^([0-9A-Fa-f]{2}[:-]){5}[0-9A-Fa-f]{2}$/.test(id))
      return id.toUpperCase();
    return id;
  };
  const formattedDeviceId = formatMac(rawDeviceId) || t('detail.notAvailable');

  const handleCopyDeviceId = async () => {
    try {
      if (!rawDeviceId) throw new Error('no-id');
      await navigator.clipboard.writeText(rawDeviceId);
      notify(
        t('detail.deviceIdCopied') ?? 'Device ID copied',
        { position: 'bottom-right' },
        'success'
      );
    } catch {
      notify(
        t('detail.copyFailed') ?? 'Copy failed',
        { position: 'bottom-right' },
        'error'
      );
    }
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="mb-6">
        <div className="flex items-start justify-between gap-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={handleGoBack} className="p-2">
              <ArrowLeft className="h-4 w-4" />
            </Button>

            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white text-lg font-bold">
                {c.firstName?.[0] ?? ''}
                {c.lastName?.[0] ?? ''}
              </div>
              <div>
                <h2 className="text-xl font-semibold">
                  {c.firstName} {c.lastName}
                </h2>
                <div className="flex gap-5">
                  <p className="text-sm text-muted-foreground">{c.email}</p>
                  {c.isAdminDeleted && (
                    <Badge
                      className="h-5 min-w-15 rounded-full px-1 "
                      variant="destructive"
                    >
                      {t('detail.deleted')}
                    </Badge>
                  )}
                  {c.isYearlyBillDisabled && (
                    <Badge className="h-5 min-w-15 rounded-full px-1 bg-orange-100 text-orange-700 px-3 py-1 rounded-full border border-orange-200 shadow-sm">
                      {t('detail.yearlyBillDisabled')}
                    </Badge>
                  )}
                </div>

                {/* Show alert when last name is missing */}
                {!c.lastName && (
                  <div className="mt-3 rounded-md bg-yellow-50 border border-yellow-200 p-3 text-yellow-800 text-sm">
                    {t(
                      'detail.missingLastNameAlert',
                      'This customer does not have a last name. Please add a last name.'
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* <Button
              variant="outline"
              onClick={() => setPopupOpen(true)}
              disabled={fetching}
            >
              {fetching ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                </>
              ) : (
                <span className="hidden sm:inline">
                  {!c.isVollmachtDocumentUploaded
                    ? t('uploadDocument')
                    : t('attorneyDocument')}
                </span>
              )}
            </Button> */}

            {/* Popup */}
            <PowerOfAttorneyPopup
              open={popupOpen}
              onOpenChange={setPopupOpen}
              hasDocument={c.isVollmachtDocumentUploaded}
              userId={customer.id}
              downloadFile={c.vollmachtUrl}
            />

            <Button variant="outline" onClick={() => setOpen(true)}>
              <span className="hidden sm:inline">
                {t('detail.viewNotification')}
              </span>
            </Button>

            <Button
              variant="outline"
              onClick={() =>
                navigate({
                  to: `/customers/$id/edit`,
                  params: { id: customer.id },
                  replace: !canGoBack,
                })
              }
            >
              <Edit className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">{t('detail.edit')}</span>
            </Button>

            {isSuperAdmin && (
              <Button
                onClick={() => giftEnergyDialog.showAlert()}
                className="bg-yellow-200 hover:bg-yellow-100 text-black border-transparent"
                aria-label={t('detail.giftEnergy')}
              >
                <Gift className="h-4 w-4 mr-2 text-black" />
                <span className="hidden sm:inline">
                  {t('detail.giftEnergy')}
                </span>
              </Button>
            )}

            {isSubscribed && !c.isCancelled && (
              <Button
                variant="destructive"
                onClick={() => cancelSubscriptionDialog.showAlert()}
                disabled={!isSubscribed}
                className="bg-rose-600 hover:bg-rose-700 text-white border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label={
                  t('detail.cancelSubscription') ?? 'Delete Customer?'
                }
              >
                <X className="h-4 w-4 mr-2 text-white" />
                <span className="hidden sm:inline">
                  {t('detail.cancelSubscription') ?? 'Delete Customer'}
                </span>
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {/* Column group: Customer Info (spans three) */}
        <Card className="lg:col-span-3">
          <CardContent className="space-y-2">
            <div className="grid gap-4 grid-cols-1 md:grid-cols-12">
              {/* Column 1: Address (reduced width) */}
              <div className="md:col-span-3 md:border-r md:pr-6 border-gray-100 space-y-6">
                {/* Address Section */}
                <div className="flex gap-3">
                  <MapPin className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold tracking-tight">
                      {t('list.address')}
                    </p>
                    <div className="mt-1 text-sm text-muted-foreground leading-snug">
                      <p className="font-medium">
                        {c.address?.street ?? c.streetAddress ?? '-'}
                      </p>
                      <p>
                        {c.address?.city ?? c.city ?? ''}
                        {c.address?.state ? `, ${c.address.state}` : ''}{' '}
                        {c.address?.zipCode ?? c.zipCode ?? ''}
                      </p>
                      <p>{c.address?.country ?? ''}</p>
                    </div>
                  </div>
                </div>
                {/* Energy Provider Section */}
                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <Zap className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold tracking-tight">
                      {t('detail.energyProvider')}
                    </p>
                    <div className="mt-1 text-sm text-muted-foreground leading-snug">
                      <p className="font-medium">
                        {c.energyProviderName ?? '-'}
                      </p>
                      <p>{c.energyProviderCustomerId ?? ''}</p>
                      <p>{c.energyProviderEmail ?? ''}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Column 2: Subscription (expanded width) */}
              <div className="md:col-span-5 flex gap-3 md:border-r md:pr-6 border-gray-100">
                <Calendar className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
                <div className="space-y-3 w-full">
                  <p className="text-sm font-semibold tracking-tight">
                    {t('detail.subscription')}
                  </p>
                  <div className="grid grid-cols-[150px_1fr] gap-y-3 text-sm">
                    <div className="text-xs text-muted-foreground">
                      {t('detail.subscriptionName')}
                    </div>
                    <div className="font-medium min-w-0">
                      {/* show truncated subscription id/name with copy and popover for full value */}
                      {(() => {
                        const handleCopySubscription = async () => {
                          try {
                            if (
                              !c.subscriptionType ||
                              c.subscriptionType === '-'
                            )
                              throw new Error('no-sub');
                            await navigator.clipboard.writeText(
                              String(c.subscriptionType)
                            );
                            notify(
                              t('detail.subscriptionCopied') ??
                                'Subscription copied',
                              { position: 'bottom-right' },
                              'success'
                            );
                          } catch {
                            notify(
                              t('detail.copyFailed') ?? 'Copy failed',
                              { position: 'bottom-right' },
                              'error'
                            );
                          }
                        };

                        return (
                          <div className="flex items-center gap-2 min-w-0">
                            <div
                              className="truncate max-w-[220px] cursor-default"
                              data-testid="subscription-value"
                              title={c.subscriptionType ?? ''}
                            >
                              {c.subscriptionType?.length
                                ? c.subscriptionType
                                : '-'}
                            </div>
                            {!!c.subscriptionType?.length && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 shrink-0"
                                onClick={handleCopySubscription}
                                aria-label={t('detail.copySubscription')}
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        );
                      })()}
                    </div>

                    <div className="text-xs text-muted-foreground">
                      {t('detail.subscriptionStatus')}
                    </div>
                    <div>
                      <Badge
                        className={getStatusBadgeClass(
                          isSubscribed ? 'active' : 'inactive'
                        )}
                      >
                        {isSubscribed
                          ? t('list.active', 'Active')
                          : t('list.inactive', 'Inactive')}
                      </Badge>
                    </div>

                    <div className="text-xs text-muted-foreground">
                      {t('detail.stromkontoSubExpiry')}
                    </div>
                    <div className="font-medium">
                      {c.subscriptionEndDate
                        ? dateFormatters.formatWithLocale(c.subscriptionEndDate)
                        : '-'}
                    </div>

                    <div className="text-xs text-muted-foreground">
                      {t('detail.stripeCustomerId')}
                    </div>
                    <div className="font-medium">
                      {c.stripeCustomerId ?? '-'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Column 3: Meter (expanded width) */}
              <div className="md:col-span-4 flex gap-3">
                <Gauge className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
                <div className="space-y-3 w-full">
                  <p className="text-sm font-semibold tracking-tight">
                    {t('detail.meterDetails')}
                  </p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                    <div className="text-xs text-muted-foreground">
                      {t('detail.meterManufacturer')}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        {c.meterManufacturer ?? '-'}
                      </span>
                    </div>

                    <div className="text-xs text-muted-foreground">
                      {t('detail.meterModel')}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{c.meterModel ?? '-'}</span>
                    </div>

                    <div className="text-xs text-muted-foreground">
                      {t('list.columns.meterStatus')}
                    </div>
                    <div className="font-medium">{c.meterStatus ?? '-'}</div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Balance Card: only Energy Balance (CHF & kWh) */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold tracking-tight">
              {t('detail.balanceSub')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">
                  {t('list.columns.energyBalance')} (kWh)
                </p>
                {customerManager.fetching ? (
                  <Skeleton className="h-6 w-30 mt-2" />
                ) : (
                  <p className="text-base font-semibold">
                    {typeof c.energyBalance === 'number'
                      ? `${c.energyBalance.toFixed(2)} kWh`
                      : '-'}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Device Details Card back to original position below */}
      <Card className="relative">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold tracking-tight flex items-center gap-2">
            <Wifi className="h-4 w-4" /> {t('detail.deviceDetails')}
          </CardTitle>
        </CardHeader>
        <CardContent className={c.deviceId ? '' : 'opacity-50'}>
          <div className="grid gap-4 md:grid-cols-3 text-sm">
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                {t('detail.deviceId')}
              </p>
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm select-all">
                  {formattedDeviceId}
                </span>
                {rawDeviceId && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={handleCopyDeviceId}
                    aria-label={t('detail.copyDeviceId')}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                {t('detail.deviceStatus')}
              </p>
              <p className="font-medium">{c.deviceState ?? '-'}</p>
            </div>
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                {t('detail.firmwareVersion')}
              </p>
              <p className="font-medium">
                {(c as unknown as { firmwareVersion?: string })
                  .firmwareVersion ?? '-'}
              </p>
            </div>
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                {t('detail.wifiSignal', 'WiFi Signal')}
              </p>
              <p className="font-medium">
                {c.wifiSignal ? `${c.wifiSignal} dBm` : '-'}
              </p>
            </div>
          </div>
        </CardContent>
        {!c.deviceId && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
            <span className="text-xs font-medium bg-orange-100 text-orange-700 px-3 py-1 rounded-full border border-orange-200 shadow-sm">
              {t('detail.deviceNotConnectedRibbon')}
            </span>
          </div>
        )}
      </Card>

      <CustomerStripeProductsSection customerId={customer.id} />

      {/* Accounts and Billing Section */}
      <div className="mt-4">
        <CustomerBillingTable />
      </div>
      {/* Usage Chart */}
      <Card className="mt-2">
        <CardHeader>
          <div className="flex items-start justify-between w-full">
            <div>
              <CardTitle>{t('list.powerData')}</CardTitle>
              <CardDescription>
                {t('list.powerDataDescription')}
              </CardDescription>
            </div>
            <div className="text-right">
              {/* Show total usage depending on selected mode */}
              <TotalUsageDisplay mode={usageMode} usageResp={usageResp} />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="max-w-full">
            {/* Usage chart with switchers */}
            <CustomerUsageChart
              mode={usageMode}
              selectedYear={usageYear}
              selectedMonth={usageMonth}
              selectedDate={usageDate}
              onModeChange={setUsageMode}
              onYearChange={(y: number) => setUsageYear(y)}
              onMonthChange={(m: number) => setUsageMonth(m)}
              onDateChange={setUsageDate}
              usageData={usageResp?.points ?? null}
              isLoading={usageQuery?.isFetching ?? false}
              isType="Line"
            />
          </div>
        </CardContent>
      </Card>

      {/* View more toggle: lazy-load heavy/detail sections */}
      <div className="mt-6">
        {!showMoreDetails ? (
          <div className="flex justify-center">
            <Button
              size="lg"
              className="px-8"
              onClick={() => setShowMoreDetails(true)}
            >
              {t('detail.viewTelemetry', 'View telemetry')}
            </Button>
          </div>
        ) : (
          <div ref={moreRef} className="mt-4 animate-[fadeIn_180ms_ease]">
            <div className="bg-surface shadow-md rounded-lg ring-1 ring-slate-100 p-4">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h3 className="text-lg font-semibold">
                    {t('detail.moreDetails', 'More details')}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {t(
                      'detail.moreDetailsDesc',
                      'Additional telemetry information and insights'
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowMoreDetails(false);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  >
                    {t('detail.hideDetails', 'Hide')}
                  </Button>
                </div>
              </div>

              <Suspense
                fallback={
                  <div className="py-6 text-center">
                    {t('loading', 'Loading...')}
                  </div>
                }
              >
                {/* Telemetry table */}
                <div className="mt-4">
                  <CustomerTelemetryTable customerId={customer.id} />
                </div>
              </Suspense>
            </div>
          </div>
        )}
      </div>

      <SharedAlertDialog
        open={cancelSubscriptionDialog.isOpen}
        onOpenChange={cancelSubscriptionDialog.setIsOpen}
        title={
          t('detail.cancelSubscriptionConfirmationTitle') ?? 'Delete Customer?'
        }
        description={
          t('detail.cancelSubscriptionConfirmationDesc') ??
          "This will cancel the customer's subscription. This action cannot be undone."
        }
        confirmText={t('detail.cancelSubscription') ?? 'Delete Customer'}
        variant="destructive"
        onConfirm={handleCancelSubscriptionConfirm}
      />
      <SharedAlertDialog
        open={giftEnergyDialog.isOpen}
        onOpenChange={() => null}
        onCancel={() => {
          setInputValue('');
          giftEnergyDialog.setIsOpen(false);
        }}
        title={t('detail.giftEnergy') ?? 'Gift Energy'}
        description={
          t('detail.giftEnergyDesc') ?? 'Enter the energy to gift (in kWh)'
        }
        confirmText={t('detail.giftEnergy') ?? 'Gift'}
        cancelText={t('detail.cancel')}
        onConfirm={() => handleGiftEnergyConfirm()}
        confirmDisabled={!inputValue || Number(inputValue) <= 0}
        showLoading={giftEnergy.isPending}
      >
        <input
          type="text"
          inputMode="decimal"
          pattern="^\d*(\.\d{0,1})?$"
          value={inputValue}
          onChange={e => {
            const val = e.target.value;
            const regex = /^\d*(\.\d{0,1})?$/;
            if (val === '' || regex.test(val)) {
              setInputValue(val);
            }
          }}
          className="w-full border rounded-md p-3 text-lg focus:outline-none focus:ring-2 focus:ring-gray-300"
          placeholder="Enter kWh..."
          min={1}
        />
      </SharedAlertDialog>

      {/* Gift Energy Confirmation Dialog */}
      <SharedAlertDialog
        open={giftEnergyConfirmDialog.isOpen}
        onOpenChange={() => null}
        onCancel={() => {
          setInputValue('');
          giftEnergyConfirmDialog.setIsOpen(false);
        }}
        title={t('detail.confirmGiftEnergy') ?? 'Confirm Gift Energy'}
        description={
          t('detail.confirmGiftEnergyDesc', { kWh: inputValue }) ??
          `Are you sure you want to gift ${inputValue} kWh to this customer?`
        }
        confirmText={t('detail.confirm') ?? 'Confirm'}
        onConfirm={handleGiftEnergyFinalConfirm}
        showLoading={giftEnergy.isPending}
      />
      {/* View Notification */}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          style={{
            width: '90vw',
            maxWidth: '1200px',
            height: 'auto',
            maxHeight: '90vh',
            minHeight: '400px',
          }}
        >
          <DialogHeader>
            <DialogTitle>Notifications</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[500px]">
            <div className="w-full">
              <CustomerNotification customerID={id} />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
